import { DataTable } from "@/components/composables/DataTable";
import { useGetAllTerminalHealth } from "../misc/mutations";
import { Checkbox } from "@/components/ui/checkbox";
import { ColumnDef } from "@tanstack/react-table";
import useControl from "@/hooks/useControl";
import { useState } from "react";
import Pagination from "../misc/pagination";
import type { TerminalHealth } from "../misc/types";
import TerminalFormModal from "../misc/addTerminalModal";
import TerminalHealthOptions from "../misc/terminalHealthOptions";
import ViewTerminalHealthModal from "../misc/viewTerminalHealthModal";

const TerminalHealths = () => {
  const {
    state: isOpenView,
    setFalse: onCloseView,
  } = useControl();
  const {
    state: isOpenViewModal,
    setTrue: onOpenViewModal,
    setFalse: onCloseViewModal,
  } = useControl();

  const [currentPage, setCurrentPage] = useState(1);

  const { data, isPending } = useGetAllTerminalHealth({
    size: 10,
    page: currentPage,
  });

  const [terminalHealth, setTerminalHealth] = useState<TerminalHealth>();
  const totalPages = data?.totalPages as number;

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const columns: ColumnDef<TerminalHealth>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          className="border-[#86929E]"
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          className="border-[#86929E]"
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "terminalId",
      header: "Terminal ID",
      cell: ({ row }) => <div>{row.original.terminalId}</div>,
    },

    {
      accessorKey: "serialNumber",
      header: "Serial Number",
      cell: ({ row }) => <div>{row.original.serialNumber}</div>,
    },
    {
      accessorKey: "terminalType",
      header: "Terminal Type",
      cell: ({ row }) => <div>{row.original.terminalType ?? "N/A"}</div>,
    },
    {
      accessorKey: "location",
      header: "Location",
      cell: ({ row }) => <div>{row.original.location}</div>,
    },
    {
      accessorKey: "printerStatus",
      header: "Printer Status",
      cell: ({ row }) => (
        <div>
          {row.original.printerStatus === "ok" ? (
            <span className="text-green-600 uppercase">Ok</span>
          ) : (
            <span className="text-red-600 uppercase">Not Ok</span>
          )}
        </div>
      ),
    },
    {
      accessorKey: "batteryLevel",
      header: "Battery Level",
      cell: ({ row }) => <div>{row.original.batteryLevel ?? "N/A"}</div>,
    },
    {
      accessorKey: "updatedAt",
      header: "Date Time",
      cell: ({ row }) => <div>{row.original.updatedAt ?? "N/A"}</div>,
    },

    {
      id: "actions",
      cell: ({ row }) => (
        <TerminalHealthOptions
          onOpenView={onOpenViewModal}
          id={row.original._id}
          setTerminalHealth={() => setTerminalHealth(row.original)}
        />
      ),
    },
  ];

  return (
    <div>
      <DataTable
        isLoading={isPending}
        columns={columns}
        data={(data?.items ?? []) as TerminalHealth[]}
      />
      <div className="flex justify-end">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>

      {isOpenView && (
        <TerminalFormModal onClose={onCloseView} isOpen={isOpenView} />
      )}
      {isOpenViewModal && (
        <ViewTerminalHealthModal
          onClose={onCloseViewModal}
          terminalHealth={terminalHealth as TerminalHealth}
        />
      )}
    </div>
  );
};

export default TerminalHealths;
